import React from "react";
import { BlockMath, InlineMath } from "../../../../components/math/KatexWrappers";
import FractionInput from "../../../../components/inputs/FractionInput";
import {
  evaluatePolynomial,
  fractionLatex,
  isCorrectNumber,
  pick,
  polynomialLatex,
  randInt,
  renderPreview,
  taskTags,
} from "./helpers";

function createIntegerData() {
  const x = pick([-3, -2, -1, 2, 3]);
  const terms = [
    { degree: 3, coefficient: pick([-2, -1, 1, 2]) },
    { degree: 2, coefficient: pick([-4, -3, 3, 4]) },
    { degree: 1, coefficient: pick([-5, -2, 2, 5]) },
    { degree: 0, coefficient: randInt(-8, 8) || 3 },
  ];
  return { terms, argumentLatex: String(x), argumentValue: x, answer: evaluatePolynomial(terms, x), variant: "integer" };
}

function createFractionData() {
  const denominator = pick([2, 3, 4]);
  const numerator = pick([-5, -3, -1, 1, 3, 5]);
  const coefficient = denominator * pick([-3, -2, 2, 3]);
  const constant = randInt(-8, 8) || 5;
  const terms = [
    { degree: 1, coefficient },
    { degree: 0, coefficient: constant },
  ];
  const argumentValue = numerator / denominator;
  return {
    terms,
    argumentLatex: fractionLatex(numerator, denominator),
    argumentValue,
    answer: evaluatePolynomial(terms, argumentValue),
    variant: "fraction",
  };
}

function createSqrtData() {
  const radicand = pick([2, 3, 5, 6, 7]);
  const coefficient = pick([-4, -3, -2, 2, 3, 4]);
  const constant = randInt(-12, 12) || 1;
  const terms = [
    { degree: 2, coefficient },
    { degree: 0, coefficient: constant },
  ];
  return {
    terms,
    argumentLatex: `\\sqrt{${radicand}}`,
    argumentValue: Math.sqrt(radicand),
    answer: coefficient * radicand + constant,
    variant: "sqrt",
    radicand,
  };
}

export function generateTask(options = {}) {
  const variant = options.variant || pick(["integer", "fraction", "sqrt"]);
  const data = variant === "integer" ? createIntegerData() : variant === "fraction" ? createFractionData() : createSqrtData();
  return {
    type: "PolynomialValueTask",
    points: 1,
    data,
    tags: taskTags(["wartosc_wielomianu", data.variant]),
  };
}

export function renderTask(task) {
  return (
    <>
      <p>Oblicz wartość wielomianu dla podanego argumentu.</p>
      <BlockMath math={`W(x)=${polynomialLatex(task.data.terms)}`} />
      <BlockMath math={`x=${task.data.argumentLatex}`} />
    </>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      label={`W(${task.data.argumentLatex})`}
      value={userAnswer?.value ?? ""}
      onChange={(value) => onChange({ ...(userAnswer || {}), value })}
      disabled={disabled}
      placeholder="np. 12"
      panelWidthDesktop={220}
    />
  );
}

export function checkAnswer(task, userAnswer) {
  const correct = isCorrectNumber(userAnswer?.value, task.data.answer);
  return { correct, points: correct ? 1 : 0 };
}

export function renderAnswer(task) {
  return <InlineMath math={`W(${task.data.argumentLatex})=${task.data.answer}`} />;
}

export function renderUserAnswer(userAnswer, task) {
  return renderPreview(userAnswer?.value, `W(${task.data.argumentLatex})`);
}

export function renderExplanation(task) {
  const substitution = task.data.terms
    .map((term) => `${term.coefficient}\\cdot (${task.data.argumentLatex})^{${term.degree}}`)
    .join("+")
    .replace(/\^\{0\}/g, "^0");
  return (
    <div>
      <p><strong>Krok 1.</strong> Podstawiamy pod zmienną podany argument.</p>
      <BlockMath math={`W(${task.data.argumentLatex})=${substitution}`} />
      {task.data.variant === "sqrt" && (
        <p>Wykorzystujemy to, że <InlineMath math={`(\\sqrt{${task.data.radicand}})^2=${task.data.radicand}`} />.</p>
      )}
      <p><strong>Krok 2.</strong> Wykonujemy rachunek.</p>
      <BlockMath math={`W(${task.data.argumentLatex})=${task.data.answer}`} />
      <p><strong>Odpowiedź:</strong> {renderAnswer(task)}.</p>
    </div>
  );
}
